import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import appSettings from '../config/settings';
import { fetchExchangeRates } from '../util/api';
import { detectCurrencyFromBrowser } from '../util/currencyLocation';
import { convertMoney, getRate } from '../util/fx';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import { useConfiguration } from './configurationContext';

const DISPLAY_CURRENCY_STORAGE_KEY = 'displayCurrency';

// A currency picked while signed in that isn't confirmed saved on the user yet: { userId, currency }.
// Kept per user, so switching accounts in the same browser can't save one user's pick on another.
const PENDING_USER_SYNC_STORAGE_KEY = 'displayCurrencyPendingUserSync';

export const CurrencyContext = createContext(null);

const getStoredDisplayCurrency = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(DISPLAY_CURRENCY_STORAGE_KEY);
    // Guard against a currency that has since been removed from the supported list.
    return appSettings.displayCurrencies.includes(stored) ? stored : null;
  } catch (e) {
    // Safari in private mode and blocked storage throw here.
    return null;
  }
};

const storeDisplayCurrency = currency => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(DISPLAY_CURRENCY_STORAGE_KEY, currency);
  } catch (e) {
    // Ignore: the picked currency just won't survive a reload.
  }
};

const getPendingUserSync = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const pending = JSON.parse(window.localStorage.getItem(PENDING_USER_SYNC_STORAGE_KEY));
    return pending?.userId && appSettings.displayCurrencies.includes(pending.currency)
      ? pending
      : null;
  } catch (e) {
    return null;
  }
};

const setPendingUserSync = pending => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (pending) {
      window.localStorage.setItem(PENDING_USER_SYNC_STORAGE_KEY, JSON.stringify(pending));
    } else {
      window.localStorage.removeItem(PENDING_USER_SYNC_STORAGE_KEY);
    }
  } catch (e) {
    // Ignore: the pick is still saved on the user, just without a retry if that fails.
  }
};

// The currency saved on the user (publicData.displayCurrency), if it's still supported.
const getUserDisplayCurrency = currentUser => {
  const currency = currentUser?.attributes?.profile?.publicData?.displayCurrency;
  return appSettings.displayCurrencies.includes(currency) ? currency : null;
};

/**
 * Resolve the currency to display prices in, in order of precedence:
 *   1. what the user picked from the switcher (stored)
 *   2. a guess based on the browser's location
 *   3. the marketplace currency
 *
 * Both of the first two are browser-only, so this returns the marketplace currency on the
 * server. Keep this the single definition of the precedence - CurrencyProvider uses it, and so
 * does anything that needs the value during its own first render.
 *
 * @param {string} marketplaceCurrency - the marketplace's own currency
 * @returns {string} currency code
 */
export const resolveDisplayCurrency = marketplaceCurrency => {
  return (
    getStoredDisplayCurrency() ||
    detectCurrencyFromBrowser(appSettings.displayCurrencies) ||
    marketplaceCurrency
  );
};

/**
 * Provides the currency the prices are displayed in, together with the live FX
 * rates needed to convert marketplace prices into it.
 *
 * Note: this only affects what buyers see. The listing price (and therefore the
 * transaction) is always in the marketplace currency.
 *
 * Until the user signs in, the picked currency only lives in this browser's storage. For a
 * signed-in user it's also saved on the user (publicData.displayCurrency), behind the scenes:
 * the UI never waits for that save. This needs the Redux store, so it's rendered inside <Provider>.
 *
 * @component
 * @param {Object} props
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const CurrencyProvider = props => {
  const { children } = props;
  const config = useConfiguration();
  const marketplaceCurrency = config?.currency;

  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserId = currentUser?.id?.uuid;
  // Read inside callbacks and effects, which must see the latest user without re-running.
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const [mounted, setMounted] = useState(false);
  // Start from the marketplace currency so that the server-rendered markup and the
  // first client render agree. The stored currency is restored after hydration.
  const [displayCurrency, setDisplayCurrencyState] = useState(marketplaceCurrency);
  const [fxState, setFxState] = useState({
    rates: null,
    date: null,
    source: null,
    isLoading: false,
    error: null,
  });

  // Resolve the currency after hydration, in order of precedence:
  //   1. what the user picked from the switcher (stored)
  //   2. a guess based on the browser's location
  //   3. the marketplace currency
  //
  // Note: the guess is deliberately not stored. Only explicit picks are, so that a
  // user who travels or changes VPN gets a fresh guess instead of a stale one.
  //
  // This also keeps the default in sync when the marketplace currency arrives with
  // the hosted configuration.
  useEffect(() => {
    setDisplayCurrencyState(resolveDisplayCurrency(marketplaceCurrency));
    setMounted(true);
  }, [marketplaceCurrency]);

  // Rates are fetched once per page load, after hydration. They are quoted against
  // the marketplace currency, so a single request covers every display currency.
  useEffect(() => {
    if (!mounted || !marketplaceCurrency) {
      return;
    }

    let isActive = true;
    setFxState(prevState => ({ ...prevState, isLoading: true, error: null }));

    fetchExchangeRates({
      base: marketplaceCurrency,
      symbols: appSettings.displayCurrencies,
    })
      .then(response => {
        if (!isActive) {
          return;
        }
        setFxState({
          rates: response?.rates || null,
          date: response?.date || null,
          source: response?.source || null,
          isLoading: false,
          error: null,
        });
      })
      .catch(e => {
        if (!isActive) {
          return;
        }
        // Without rates we fall back to showing prices in the marketplace currency.
        setFxState({ rates: null, date: null, source: null, isLoading: false, error: e });
      });

    return () => {
      isActive = false;
    };
  }, [mounted, marketplaceCurrency]);

  // Saves run one at a time, and only the latest pick waiting in line is sent. This way the
  // last pick is the one that ends up on the user, even when the user switches currency again
  // before the previous save has finished.
  const userSyncRef = useRef({ inFlight: false, queued: null });

  const saveToUser = useCallback(
    (userId, currency) => {
      const sync = userSyncRef.current;
      sync.queued = { userId, currency };
      if (sync.inFlight) {
        return;
      }

      const flush = () => {
        const next = sync.queued;
        sync.queued = null;
        if (!next) {
          sync.inFlight = false;
          return;
        }
        // Signed out or switched accounts since the pick: it stays pending for that user.
        if (next.userId !== currentUserRef.current?.id?.uuid) {
          flush();
          return;
        }

        sync.inFlight = true;
        dispatch(updateProfile({ publicData: { displayCurrency: next.currency } }))
          .unwrap()
          .then(() => {
            // Only clear the marker if no newer pick is waiting to be saved.
            const pending = getPendingUserSync();
            if (pending?.userId === next.userId && pending?.currency === next.currency) {
              setPendingUserSync(null);
            }
          })
          .catch(() => {
            // The marker stays, so the save is retried after the next sign-in or page load.
          })
          .finally(flush);
      };

      flush();
    },
    [dispatch]
  );

  // Once the signed-in user is known (after signing in, or on page load), line the displayed
  // currency up with the one saved on the user:
  //   1. a pick not yet saved on this user (the save failed or never finished) is saved now
  //   2. otherwise the currency saved on the user wins, so it follows them across devices
  //   3. a user without one gets the currency they picked in this browser, if they picked one.
  //      A browser-location guess isn't saved, for the same reason it isn't stored locally.
  useEffect(() => {
    if (!mounted || !currentUserId) {
      return;
    }

    const pending = getPendingUserSync();
    const userCurrency = getUserDisplayCurrency(currentUserRef.current);

    if (pending?.userId === currentUserId) {
      setDisplayCurrencyState(pending.currency);
      storeDisplayCurrency(pending.currency);
      if (pending.currency === userCurrency) {
        setPendingUserSync(null);
      } else {
        saveToUser(currentUserId, pending.currency);
      }
      return;
    }

    if (userCurrency) {
      setDisplayCurrencyState(userCurrency);
      storeDisplayCurrency(userCurrency);
      return;
    }

    const pickedCurrency = getStoredDisplayCurrency();
    if (pickedCurrency) {
      setPendingUserSync({ userId: currentUserId, currency: pickedCurrency });
      saveToUser(currentUserId, pickedCurrency);
    }
  }, [mounted, currentUserId, saveToUser]);

  const setDisplayCurrency = useCallback(
    currency => {
      if (!appSettings.displayCurrencies.includes(currency)) {
        return;
      }
      // The UI and this browser update straight away...
      setDisplayCurrencyState(currency);
      storeDisplayCurrency(currency);

      // ...and a signed-in user's pick is saved on the user behind the scenes.
      const userId = currentUserRef.current?.id?.uuid;
      if (userId) {
        setPendingUserSync({ userId, currency });
        saveToUser(userId, currency);
      }
    },
    [saveToUser]
  );

  const { rates } = fxState;

  const value = useMemo(() => {
    // Convert a Money entity to the currently selected display currency.
    // Returns null when the conversion can't be made (rates missing/not loaded yet).
    const convertToDisplayCurrency = money => {
      if (!money || money.currency === displayCurrency) {
        return money || null;
      }
      const rate = getRate(rates, money.currency, displayCurrency);
      return rate ? convertMoney(money, displayCurrency, rate) : null;
    };

    return {
      ...fxState,
      mounted,
      marketplaceCurrency,
      displayCurrency,
      setDisplayCurrency,
      supportedCurrencies: appSettings.displayCurrencies,
      isDisplayCurrencyMarketplaceCurrency: displayCurrency === marketplaceCurrency,
      getRate: (from, to) => getRate(rates, from, to),
      convertToDisplayCurrency,
    };
  }, [fxState, rates, mounted, displayCurrency, marketplaceCurrency, setDisplayCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

/**
 * Access the display currency and the live FX rates.
 * Returns null outside of a CurrencyProvider (e.g. in some unit tests).
 */
export const useCurrency = () => {
  return useContext(CurrencyContext);
};
